/**
 * Product Dev cost sheet — the single source of truth for the cost → landed →
 * price → margin waterfall. Pure functions, no I/O; shared by the frontend
 * Commercials card (live recompute as you type) and reusable server-side.
 *
 * Industry conventions (see .planning/reference/product-dev.md):
 *  - Fabric is a FORMULA: consumption × price/unit × (1 + wastage%), not a flat
 *    number. Falls back to the legacy flat `fabricCost` when the formula inputs
 *    are empty, so existing records keep working.
 *  - Ex-works (FOB build-up) = fabric + trim + CMT + decoration + packaging.
 *    Freight is NOT part of ex-works — it is a landed adder.
 *  - Duty is applied to the FOB/ex-works value, ADDITIVELY (duty% × exWorks),
 *    not as a gross-up division. (This fixes the old `/(1 − duty%)` bug.)
 *  - Landed = exWorks + freight + duty + amortised per-order costs (sample).
 *  - Margin is computed against LANDED cost (freight+duty of 15–25% silently
 *    sink an FOB-based margin). Forward: price = landed / (1 − targetMargin%).
 *    Backward: GPM = (retail − landed) / retail.
 */
const n = (v) => (v == null || Number.isNaN(v) ? 0 : v);
/** Round money to 2dp without float drift. */
const money = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
export function computeCommercials(input) {
    const hasFormula = input.fabricConsumption != null && input.fabricPricePerUnit != null;
    const fabricLine = hasFormula
        ? money(n(input.fabricConsumption) *
            n(input.fabricPricePerUnit) *
            (1 + n(input.fabricWastagePct) / 100))
        : money(n(input.fabricCost));
    const exWorks = money(fabricLine + n(input.trimCost) + n(input.cmtCost) + n(input.decorationCost) + n(input.packagingCost));
    const orderQty = n(input.costingOrderQty);
    const sampleAmortisation = orderQty > 0 ? money(n(input.samplePrice) / orderQty) : 0;
    const duty = money(exWorks * (n(input.dutyPct) / 100));
    const landed = money(exWorks + n(input.freightCost) + duty + sampleAmortisation);
    const targetMargin = input.targetMarginPct;
    const suggestedPrice = targetMargin != null && targetMargin < 100 && landed > 0
        ? money(landed / (1 - targetMargin / 100))
        : null;
    const retail = input.retailPrice;
    const impliedMarginPct = retail != null && retail > 0 ? Math.round(((retail - landed) / retail) * 1000) / 10 : null;
    const quoteVariance = input.agreedPrice != null ? money(input.agreedPrice - exWorks) : null;
    return {
        fabricLine,
        fabricFromFormula: hasFormula,
        exWorks,
        sampleAmortisation,
        duty,
        landed,
        suggestedPrice,
        impliedMarginPct,
        quoteVariance,
    };
}
